import { useLocale, useT } from '../i18n/locale-context';
import { formatDateNumeric } from '../lib/format';
import { pressForCountry } from '../content/press';

/**
 * «О нас пишут» — third-party coverage of the service, on `/about`.
 *
 * One row per mention in the page's own card style (`rounded-lg border-edge
 * bg-bone-surface`), not the `ui/card.tsx` variants — `/about` predates that
 * component and mixing the two would put two different card borders on one page.
 *
 * Renders nothing at all when the deployment has no mentions, so the KZ build
 * shows no empty heading (see `pressForCountry`).
 *
 * The date goes through `formatDateNumeric` (dotted `DD.MM.YYYY`, no `Intl`)
 * for the reason that helper documents: Chrome ships no `uz` CLDR data, so any
 * locale-resolved month name renders as a root-fallback string in the browser
 * while passing every Node-side test. A numeric date also renders identically
 * in SSR, prerender and hydration, so it cannot cause a hydration mismatch —
 * which matters here because `/about` is prerendered (`scripts/prerender.ts`
 * lists it in `STATIC_ROUTES`).
 */
export function PressBlock() {
  const t = useT();
  const { locale } = useLocale();
  const mentions = pressForCountry();
  if (mentions.length === 0) return null;

  return (
    <section className="mb-8">
      <h2 className="mb-2 font-brand text-[17px] font-bold text-ink">{t('about.press.title')}</h2>
      <ul className="space-y-2">
        {mentions.map((m) => (
          <li key={m.id} className="rounded-lg border border-edge bg-bone-surface p-4">
            <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12.5px] text-ink-muted">
              <span className="font-bold text-ink-soft">{m.outlet}</span>
              <span aria-hidden="true">·</span>
              <time dateTime={m.date}>{formatDateNumeric(m.date)}</time>
            </div>
            {/*
             * `rel="noopener"` and NOT `nofollow`: this is editorial coverage we
             * are citing, not a paid or user-submitted link, so the outbound link
             * is followed. No `utm_*` either — the destination is someone else's
             * analytics, and a tracking suffix on a cited URL just makes the link
             * differ from the one the article actually lives at.
             */}
            <a
              href={m.url[locale] ?? m.url.uz}
              target="_blank"
              rel="noopener"
              className="text-[14px] font-medium leading-[1.5] text-brand hover:underline"
            >
              {m.title[locale]}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
